import { Text } from "@chakra-ui/react";
import { ColumnDefWithTooltip } from "src/components/Table/Table";
import { formatUSPhoneNumber } from "src/services/format";
import { getReferenceId } from "src/services/id";
import { ColumnId } from "./constants";

type Row = {
  guardian: {
    id: uuid;
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
    referenceId?: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    emailAddress: string | null;
    phoneNumber: string | null;
    preferredLanguage: string | null;
  } | null;
};

const getGuardianColumnProps = (columnId: ColumnId) => {
  switch (columnId) {
    case ColumnId.GuardianFirstName:
      return {
        header: "Guardian First Name",
        valueFn: (person: Row["guardian"]) => person?.firstName,
      };
    case ColumnId.GuardianMiddleName:
      return {
        header: "Guardian Middle Name",
        valueFn: (person: Row["guardian"]) => person?.middleName,
      };
    case ColumnId.GuardianLastName:
      return {
        header: "Guardian Last Name",
        valueFn: (person: Row["guardian"]) => person?.lastName,
      };
    case ColumnId.GuardianId:
      return {
        header: "Guardian ID",
        valueFn: (person: Row["guardian"]) =>
          person &&
          getReferenceId({
            id: person.id,
            reference_id: person.referenceId ?? null,
          }),
      };
    case ColumnId.GuardianAddress:
      return {
        header: "Guardian Address",
        valueFn: (person: Row["guardian"]) => {
          if (!person) return null;
          const { address, city, state, zip } = person;
          return (
            <>
              <Text>{address}</Text>
              <Text>{`${city}, ${state} ${zip}`}</Text>
            </>
          );
        },
      };
    case ColumnId.GuardianEmail:
      return {
        header: "Guardian Email",
        valueFn: (person: Row["guardian"]) => person?.emailAddress,
      };
    case ColumnId.GuardianPhone:
      return {
        header: "Guardian Phone",
        valueFn: (person: Row["guardian"]) =>
          person?.phoneNumber ? formatUSPhoneNumber(person.phoneNumber) : null,
      };
    case ColumnId.GuardianLanguage:
      return {
        header: "Guardian Preferred Language",
        valueFn: (person: Row["guardian"]) => person?.preferredLanguage,
      };
    default:
      throw new Error(`Unknown column ID: ${columnId}`);
  }
};

export function buildGuardianColumnDef<T extends Row>(
  columnId: ColumnId
): ColumnDefWithTooltip<T> {
  const { header, valueFn } = getGuardianColumnProps(columnId);
  return {
    id: columnId,
    header,
    cell: (props) => {
      const guardian = props.row.original.guardian;
      return valueFn(guardian);
    },
  };
}
